import React from 'react';

import { PickupDropoffReservation } from '@/types/pickupDropoff';
import { Vehicle } from '@/types/vehicle';

type Props = {
  reservations: PickupDropoffReservation[];
  vehicles: Vehicle[];
};

interface ScheduleItem {
  no: number;
  childName: string;
  time: string;
  location: string;
}

interface VehicleSchedule {
  name: string;
  pickup: ScheduleItem[];
  dropoff: ScheduleItem[];
}

interface Schedule {
  date: string;
  vehicles: VehicleSchedule[];
}

const PickupDropoffReservationsTable = ({ reservations, vehicles }: Props) => {
  console.log('reservations', reservations, vehicles);
  // 表示対象の車種IDリストを作成
  const allowedVehicleIds = vehicles.map((v) => v.id);

  const convertToSchedule = (
    reservations: PickupDropoffReservation[],
    vehicles: Vehicle[]
  ): Schedule[] => {
    const result: { [key: string]: Schedule } = {};

    // 表示対象の車種に関連する予約のみをフィルタリング
    const filteredReservations = reservations.filter((reservation) => {
      // 来所情報か退所情報の少なくとも一方が表示対象の車種であるか確認、または vehicle_id が未設定の場合
      const pickupAllowed =
        reservation.pickup_info &&
        (!reservation.pickup_info.vehicle_id ||
          allowedVehicleIds.includes(reservation.pickup_info.vehicle_id));
      const dropoffAllowed =
        reservation.dropoff_info &&
        (!reservation.dropoff_info.vehicle_id ||
          allowedVehicleIds.includes(reservation.dropoff_info.vehicle_id));

      // 来所または退所のいずれかが対象の車種の予約を表示する、または vehicle_id が未設定
      return pickupAllowed || dropoffAllowed;
    });

    // 日付ごとに処理
    filteredReservations
      .filter((reservation) => reservation.pickup_info || reservation.dropoff_info)
      .forEach((reservation) => {
        if (!result[reservation.reservation_date]) {
          result[reservation.reservation_date] = {
            date: reservation.reservation_date,
            vehicles: [],
          };
        }

        // 来所情報の処理
        if (reservation.pickup_info) {
          // 車種名の決定: vehicle_id がない場合は「配車未登録」とする
          let vehicleName = '配車未登録';

          if (reservation.pickup_info.vehicle_id) {
            // vehicle_id がある場合
            // 来所の車種が表示対象に含まれている場合のみ処理
            if (allowedVehicleIds.includes(reservation.pickup_info.vehicle_id)) {
              vehicleName =
                vehicles.find((v) => v.id === reservation.pickup_info?.vehicle_id)?.name ||
                '配車未登録';
            } else {
              // 表示対象でない車種の場合はスキップ
              return;
            }
          }

          // 車種エントリーを見つけるか作成
          let vehicle = result[reservation.reservation_date].vehicles.find(
            (v) => v.name === vehicleName
          );

          if (!vehicle) {
            vehicle = {
              name: vehicleName,
              pickup: [],
              dropoff: [],
            };
            result[reservation.reservation_date].vehicles.push(vehicle);
          }

          // 来所スケジュールを追加
          vehicle.pickup.push({
            no: vehicle.pickup.length + 1,
            childName: reservation.child_name,
            time: reservation.pickup_info.time.substring(0, 5),
            location: reservation.pickup_info.location,
          });
        }

        // 退所情報の処理
        if (reservation.dropoff_info) {
          // 車種名の決定: vehicle_id がない場合は「配車未登録」とする
          let vehicleName = '配車未登録';

          if (reservation.dropoff_info.vehicle_id) {
            // vehicle_id がある場合
            // 退所の車種が表示対象に含まれている場合のみ処理
            if (allowedVehicleIds.includes(reservation.dropoff_info.vehicle_id)) {
              vehicleName =
                vehicles.find((v) => v.id === reservation.dropoff_info?.vehicle_id)?.name ||
                '配車未登録';
            } else {
              // 表示対象でない車種の場合はスキップ
              return;
            }
          }

          // 車種エントリーを見つけるか作成
          let vehicle = result[reservation.reservation_date].vehicles.find(
            (v) => v.name === vehicleName
          );

          if (!vehicle) {
            vehicle = {
              name: vehicleName,
              pickup: [],
              dropoff: [],
            };
            result[reservation.reservation_date].vehicles.push(vehicle);
          }

          // 退所スケジュールを追加
          vehicle.dropoff.push({
            no: vehicle.dropoff.length + 1,
            childName: reservation.child_name,
            time: reservation.dropoff_info.time.substring(0, 5),
            location: reservation.dropoff_info.location,
          });
        }
      });

    return Object.values(result);
  };

  const schedules = convertToSchedule(reservations, vehicles);

  // Sort vehicles for consistency in display
  schedules.forEach((schedule) => {
    schedule.vehicles.sort((a, b) => a.name.localeCompare(b.name));
  });

  return (
    <table className="w-full border-collapse">
      {schedules.map((schedule) => (
        <React.Fragment key={schedule.date}>
          <thead>
            <tr>
              <th
                colSpan={8}
                className="border border-gray-300 bg-gray-100 p-2 text-left"
              >
                {schedule.date}
              </th>
            </tr>
          </thead>
          {schedule.vehicles.map((vehicle) => (
            <React.Fragment key={vehicle.name}>
              <thead>
                <tr>
                  <th
                    colSpan={8}
                    className="border border-gray-300 bg-gray-50 p-2 text-left"
                  >
                    {vehicle.name}
                  </th>
                </tr>
                <tr>
                  <th
                    colSpan={4}
                    className="border border-gray-300 p-2"
                  >
                    送迎(来所)
                  </th>
                  <th
                    colSpan={4}
                    className="border border-gray-300 p-2"
                  >
                    送迎(退所)
                  </th>
                </tr>
                <tr>
                  <th className="border border-gray-300 p-2">No</th>
                  <th className="border border-gray-300 p-2">利用者(児童)</th>
                  <th className="border border-gray-300 p-2">到着予定</th>
                  <th className="border border-gray-300 p-2">送迎場所</th>
                  <th className="border border-gray-300 p-2">No</th>
                  <th className="border border-gray-300 p-2">利用者(児童)</th>
                  <th className="border border-gray-300 p-2">到着予定</th>
                  <th className="border border-gray-300 p-2">送迎場所</th>
                </tr>
              </thead>
              <tbody>
                {/* Calculate max rows needed for this vehicle */}
                {Array.from({
                  length: Math.max(vehicle.pickup.length, vehicle.dropoff.length),
                }).map((_, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 p-2">
                      {vehicle.pickup[index]?.no || ''}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {vehicle.pickup[index]?.childName || ''}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {vehicle.pickup[index]?.time || ''}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {vehicle.pickup[index]?.location || ''}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {vehicle.dropoff[index]?.no || ''}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {vehicle.dropoff[index]?.childName || ''}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {vehicle.dropoff[index]?.time || ''}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {vehicle.dropoff[index]?.location || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </table>
  );
};

export default PickupDropoffReservationsTable;
